/**
 * Defines reducer for current user's edit state
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Yucho Ho
 */
import isEmpty from 'lodash/isEmpty'
import {
  UPDATE_BOARD_EDIT_STATE,
  UPDATE_LIST_EDIT_STATE,
  UPDATE_PLACE_EDIT_STATE,
  CLEAR_EDIT_STATE,
  RECEIVE_CONNECT_USER
} from '../../types'

const initialState = {}

const isEmptyOrFalsy = (value) => {
  if (!value) {
    return true
  }

  if (typeof value === 'object') {
    return isEmpty(value)
  }
}

const deleteEmptyOrFalsy = (subState) => {
  for (const key of Object.keys(subState)) {
    if (isEmptyOrFalsy(subState[key])) {
      delete subState[key]
    }
  }
  return subState
}

const mergeAndClean = (state, formattedData, depth) => {
  const mergeNestedStates = (subState, subData, currDepth) => {
    if (currDepth === 0) {
      const newState = Object.assign({}, subState, subData)
      return deleteEmptyOrFalsy(newState)
    }

    const newState = { ...subState }
    for (const key of Object.keys(subData)) {
      newState[key] = mergeNestedStates(subState[key] || {}, subData[key], currDepth - 1)
    }
    return deleteEmptyOrFalsy(newState)
  }

  return mergeNestedStates(state, formattedData, depth)
}

const updateHandler = (formatData, depth) => {
  return (state, action) => {
    const formattedData = formatData(action.data)

    return mergeAndClean(state, formattedData, depth)
  }
}

const formatBoardData = (data) => {
  return { board: data }
}

const formatListsData = (data) => {
  return formatBoardData({ lists: data })
}

const formatPlacesData = (data) => {
  let formattedData = {}
  for (const lid of Object.keys(data)) {
    formattedData[lid] = {
      places: data[lid]
    }
  }
  return formatListsData(formattedData)
}

// Depth of nested data
const boardDepth = 1
const listsDepth = boardDepth + 2
const placesDepth = listsDepth + 2

const ACTION_HANDLERS = {
  [RECEIVE_CONNECT_USER]: (state, action) => {
    if (action.data.uid === action.currentUserId) {
      const newState = { ...state, ...action.data }
      delete newState.uid

      return newState
    }
    return state
  },
  [UPDATE_BOARD_EDIT_STATE]: updateHandler(formatBoardData, boardDepth),
  [UPDATE_LIST_EDIT_STATE]: updateHandler(formatListsData, listsDepth),
  [UPDATE_PLACE_EDIT_STATE]: updateHandler(formatPlacesData, placesDepth),
  [CLEAR_EDIT_STATE]: () => initialState
}

export default (state = initialState, action) => {
  const handler = ACTION_HANDLERS[action.type]

  return handler ? handler(state, action) : state
}
